import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AngularTokenService } from '@raysuelzer/angular-token';
import { isNil } from 'lodash-es';
import { Observable, of as observableOf, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap, throttleTime } from 'rxjs/operators';

import { ToastrService } from 'core/toastr.service';
import { getSafe } from 'helpers';
import { GetCurrentUserGQL } from 'generated/graphql';

import { setCurrentUser } from '../../_state/auth/auth-actions';
import { AuthState } from '../../_state/auth/auth-state';

// Create a shared subject and validation observable outside the guard function
const subject = new ReplaySubject<boolean>(1);
let validationObservable: Observable<boolean>;

export const setAppStateForLoggedInUser = (
  getCurrentUserGQL: GetCurrentUserGQL,
  store: Store<AuthState>
): Observable<any> => {
  return getCurrentUserGQL.fetch().pipe(
    map(({ data }) => data.getCurrentUser),
    tap(currentUser => {
      store.dispatch(setCurrentUser({ payload: currentUser }));
    })
  );
};

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const store = inject(Store<AuthState>);
  const authService = inject(AngularTokenService);
  const router = inject(Router);
  const toastrService = inject(ToastrService);
  const getCurrentUserGQL = inject(GetCurrentUserGQL);

  if (!validationObservable) {
    const authServiceValidation$ = authService.validateToken()
      .pipe(
        map<any, boolean>((res: any) => res.success as boolean),
        catchError(res => {
          const responseIs4xx = getSafe(() => res.status.toString(), '').match(/4\d\d/);
          if (navigator.onLine === false || !responseIs4xx) {
            toastrService.warning('Please check your internet connection.');
            return observableOf(true);
          } else {
            return observableOf(res.success as boolean);
          }
        }),
        switchMap((loggedIn) => setAppStateForLoggedInUser(getCurrentUserGQL, store).pipe(
          switchMap(() => observableOf(loggedIn)),
          catchError(() => observableOf(loggedIn))
        )),
        tap((loggedIn) => {
          if (!loggedIn) {
            router.parseUrl('/login');
          }
        })
      );

    validationObservable = subject.pipe(
      throttleTime(10000),
      switchMap(() => authServiceValidation$),
      shareReplay<boolean>()
    );
  }

  // Trigger validation observable
  subject.next(null);

  return validationObservable.pipe(
    switchMap(tokenValid => {
      if (authService.currentUserData === undefined) {
        return authService.validateToken();
      }
      return observableOf(tokenValid);
    }),
    switchMap((tokenValid) => {
      if (tokenValid === false) {
        return observableOf(false);
      } else {
        const roleOk = checkRole(route);
        if (!roleOk) {
          redirectUnauthorized(route, state);
          return observableOf(false);
        }
        return observableOf(true);
      }
    })
  );

  function checkRole(rt: ActivatedRouteSnapshot): boolean {
    const currentUserData = authService.currentUserData;
    const canActivateRoles: Array<string> = rt.data['canActivateRoles'];
    if (isNil(canActivateRoles)) {
      return true;
    }
    if (canActivateRoles.includes(currentUserData['role'])) {
      return true;
    }
    return false;
  }

  function redirectUnauthorized(rt: ActivatedRouteSnapshot, st: RouterStateSnapshot): void {
    const unauthRedirectUrl = rt.data['unauthorizedRedirectUrl'];
    if (unauthRedirectUrl) {
      router.navigate([unauthRedirectUrl], { queryParamsHandling: 'preserve' });
    } else {
      if (st.url.toLowerCase() !== 'select-campaign') {
        router.navigateByUrl('select-campaign');
      }
    }
  }
};
